import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react'
import { Alert, Container, Modal, Button, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router';
import { Frameworks } from '../../api/Frameworks';
import { Services } from '../../api/Services';
import AppSearch from '../../components/common/AppSearch';
import ServiceItem from '../../components/list-items/ServiceItem';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import { Checkbox, Pagination, Select } from "antd";
import { TailSpin } from "react-loader-spinner";
import { debounce } from "lodash";
import { useMemo } from "react";
import { useAuth } from "../../contexts/auth";
import { accessLevelOptions } from '../../utils';
import { BdaService } from '../../models/BdaService';
import { Option, PagedResults } from '../../models/generics';
import { CheckboxChangeEvent } from 'antd/es/checkbox';


interface FrameworkOption {
  label: string;
  value: number;
  image: string;
  trailing: string;
}

export default function ServicesList() {
  const [services, setServices] = useState<BdaService[]>();
  const [totalServices, setTotalServices] = useState<number>();
  const [error, setError] = useState(false);

  const [searchText, setSearchText] = useState('');
  const [modeFilter, setModeFilter] = useState<string>();
  const [areaFilter, setAreaFilter] = useState<string>();
  const [myServices, setMyServices] = useState(false);
  const [accessLevel, setAccessLevel] = useState<string>();
  const [framework, setFramework] = useState<number>();

  const [frameworkOptions, setFrameworkOptions] = useState<FrameworkOption[]>();
  const [modesOptions, setModesOptions] = useState<Option[]>();
  const [areasOptions, setAreasOptions] = useState<Option[]>();

  const [itemsPerPage, setItemsPerPage] = useState(40);
  const [currentPage, setCurrentPage] = useState(1);

  const [sortDesc, setSortDesc] = useState(false);

  const [serviceToDelete, setServiceToDelete] = useState<BdaService>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleClose = () => { setShowDeleteModal(false); setServiceToDelete(undefined) };

  const { loggedUser } = useAuth();
  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();
  const navigate = useNavigate();

  useEffect(() => {
    initFrameworks();
    initModes();
    initAreas();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    reset();
    // eslint-disable-next-line
  }, [currentPage])

  // handling current page higher than actual available pages edge case
  useEffect(() => {
    if (currentPage > 1) setCurrentPage(1);
    else reset();
    // eslint-disable-next-line
  }, [itemsPerPage, sortDesc, searchText, modeFilter, areaFilter, framework, accessLevel, myServices])

  const getServices = () => {
    const params = new URLSearchParams();
    params.set("page", `${currentPage}`);
    params.set("size", `${itemsPerPage}`);
    params.set("sort", "id");
    params.set("desc", `${sortDesc}`);
    if (myServices) params.set("createdBy", loggedUser!.userId!);
    if (modeFilter) params.set("modes", modeFilter);
    if (areaFilter) params.set("areas", areaFilter);
    if (framework) params.set("frameworkIds", `${framework}`);
    if (accessLevel) params.set("accessLevel", accessLevel);
    if (searchText.length) params.set("freeText", searchText);

    Services.getCatalogueServices(params)
      .then(
        ({ data: servicesPages }) => {
          setServices((servicesPages as PagedResults<BdaService>).content);
          setTotalServices((servicesPages as PagedResults<BdaService>).pageMetadata.totalElements);
        },
        (err) => {
          showToast('services-error', 'Could not get services', 'Services Error', 'danger');
          setServices([]);
          setError(true);
        }
      );
  }

  const initFrameworks = () => {
    Frameworks.getFrameworks()
      .then(
        ({ data: frameworks }) => {
          setFrameworkOptions(
            frameworks.map(f => ({
              label: f.name,
              value: f.id,
              image: f.imageUrl,
              trailing: f.version,
            }))
          );
        },
        (err) => {
          showToast('frameworks-error', 'Could not get frameworks', 'Frameworks Error', 'danger');
          setFrameworkOptions([]);
        }
      )
  }

  const initModes = () => {
    Services.getModes()
      .then(
        ({ data: modes }) => {
          setModesOptions(
            modes.map(mode => ({
              label: mode.split("_").join(" "),
              value: mode,
            }))
          )
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('modes-error', message, `Modes Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const initAreas = () => {
    Services.getAreas()
      .then(
        ({ data: areas }) => {
          setAreasOptions(
            areas.map(area => ({
              label: area.split("_").join(" "),
              value: area,
            }))
          )
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('areas-error', message, `Areas Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const handleMyServicesChange = (evt: CheckboxChangeEvent) => setMyServices(evt.target.checked);
  const navigateToCreation = () => navigate('/services/create');
  const navigateToDetail = (id: number) => () => navigate(`/services/${id}`);
  const changeSortMode = () => setSortDesc(!sortDesc);

  const editService = (id: number) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    navigate(`/services/${id}/edit`);
  };

  const reset = () => {
    setError(false);
    setServices(undefined);
    getServices();
  }

  const openDeleteModal = (service: BdaService) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    setShowDeleteModal(true);
    setServiceToDelete(service);
  }

  const deleteService = () => {
    showLoader();

    Services.deleteService(serviceToDelete!.id!)
      .then(
        () => {
          showToast('service-delete-success', 'Service deleted successfully.', 'Success', 'success');
          hideLoader();
          handleClose();
          reset();
        },
        () => {
          hideLoader();
          showToast('services-error', 'Could not delete service', 'Service Error', 'danger');
        }
      )
  }

  const handleFilterChange = (filter: 'search' | 'area' | 'mode' | 'framework' | 'accessLevel') => (evt: any) => {
    switch (filter) {
      case 'search':
      default:
        setSearchText(evt.target.value);
        break;

      case 'area':
        setAreaFilter(evt);
        break;

      case 'mode':
        setModeFilter(evt);
        break;

      case 'framework':
        setFramework(evt);
        break;

      case 'accessLevel':
        setAccessLevel(evt);
        break;
    }
  }

  const debounceSearchChange = useMemo(() => debounce(handleFilterChange("search"), 500), []);

  const handleItemsPerPageChange = (page: number, size: number) => {
    changePage(page);
    setItemsPerPage(size);
  }
  const changePage = (page: number) => setCurrentPage(page);

  // cleanup debounced handler
  useEffect(() => {
    return () => {
      debounceSearchChange.cancel();
    }
  }, [debounceSearchChange]);

  const renderAsyncContent = () => {
    if (!services) return (
      <div className="d-flex justify-content-center py-5 my-5">
        <TailSpin
          height={100}
          width={100}
          color="#677ab1"
          radius="1"
        />
      </div>
    )

    if (error) return (
      <Alert variant="danger" className="py-2">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occured, please try again later.</span>
      </Alert>
    );

    return services.length === 0
      ? (
        <Alert variant="info" className="py-2">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">No service found.</span>
        </Alert>
      )
      : (
        <Row>
          {
            services.map(service => (
              <Col xs={12} sm={6} md={4} lg={3} className="p-2" key={service.id}>
                <ServiceItem
                  service={service}
                  onClick={navigateToDetail(service.id!)}
                  onServiceEdit={editService(service.id!)}
                  onServiceDelete={openDeleteModal(service)}
                />
              </Col>
            ))
          }
        </Row>
      )
  }

  const renderToolbar = () => {
    return (
      <>
        <Row className="mb-2 align-items-center">
          <Col>
            <h4 className="mb-0">Services List</h4>
          </Col>

          <Col xs={6} md={4} className="text-end">
            <Button size="sm" variant="success" onClick={navigateToCreation}>
              <FontAwesomeIcon icon={solid('plus')} />
              <span className="d-none d-sm-inline ms-2">Register Service</span>
            </Button>
            {/* <Button size="sm" variant="primary" className="ms-2">
              <FontAwesomeIcon icon={solid('cloud-upload-alt')} />
            </Button> */}
          </Col>
        </Row>
        <Row>
          <Col className="mb-2" md={4}>
            <AppSearch placeholder="Search service..." onChange={debounceSearchChange} />
          </Col>
          <Col className="mb-2" md={2}>
            <Select
              allowClear
              loading={!modesOptions}
              placeholder="Select mode"
              onChange={handleFilterChange('mode')}
              options={modesOptions}
              value={modeFilter}
              className="w-100"
            />
          </Col>
          <Col className="mb-2" md={2}>
            <Select
              allowClear
              loading={!areasOptions}
              placeholder="Select area"
              onChange={handleFilterChange('area')}
              options={areasOptions}
              value={areaFilter}
              className="w-100"
            />
          </Col>
          <Col className="mb-2" md={2}>
            <Select
              loading={!frameworkOptions}
              placeholder="Select framework"
              value={framework}
              allowClear
              onChange={handleFilterChange('framework')}
              className="w-100"
            >
              {frameworkOptions?.map((option, index) => (
                <Select.Option key={index} value={option.value} label={option.label}>
                  <div className="d-flex align-items-center">
                    <div className="framework-image" style={{ backgroundImage: `url(${option.image})` }}></div>
                    <span className="ms-2">{option.label}</span>
                    <small className="text-muted ms-3">{option.trailing}</small>
                  </div>
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col className="mb-2" md={2}>
            <Select
              placeholder="Select privacy level"
              value={accessLevel}
              allowClear
              onChange={handleFilterChange('accessLevel')}
              className="w-100"
            >
              {accessLevelOptions?.map((option, index) => (
                <Select.Option key={index} value={option.value} label={option.label}>
                  <FontAwesomeIcon icon={option.icon} fixedWidth />
                  <span className="ms-2">{option.label}</span>
                </Select.Option>
              ))}
            </Select>
          </Col>
        </Row>
        <Row>
          <Col md={2}>
            <Checkbox checked={myServices} onChange={handleMyServicesChange}>My Services</Checkbox>
            {/* <Checkbox className="ms-3" checked={defaultOnly} onChange={handleDefaultOnlyChange}>Default Only</Checkbox>
            <Checkbox className="ms-3" checked={activeSchedules} onChange={handleSchedulesChange}>Active Schedules</Checkbox> */}
          </Col>
          <Col className="text-center">
            <Pagination
              disabled={!services}
              total={totalServices || 0}
              showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
              pageSize={itemsPerPage}
              current={currentPage}
              onChange={changePage}
              onShowSizeChange={handleItemsPerPageChange}
            />
          </Col>
          <Col md={2}>
            <Button size="sm" variant="light" className="w-100 border" onClick={changeSortMode}>
              <FontAwesomeIcon icon={sortDesc ? solid('sort-numeric-down-alt') : solid('sort-numeric-down')} />
              <span className="ms-2">Sort: <b>{sortDesc ? "descending" : "ascending"}</b></span>
            </Button>
          </Col>
        </Row>
      </>
    )
  }

  return (
    <Container fluid="md" className="mt-3">
      {renderToolbar()}

      <hr />

      {renderAsyncContent()}

      <Modal show={showDeleteModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Service</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete service <b>{serviceToDelete && serviceToDelete.name}</b>?</p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleClose}>
            Close
          </Button>
          <Button variant="danger" size="sm" onClick={deleteService}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}
